import { RefObject, useLayoutEffect, useState } from "react";
import { useScroll, useTransform } from "framer-motion";

export default function useCustomScrollbar(
  scrollContainerRef: RefObject<HTMLDivElement>,
  scrollTrackRef: RefObject<HTMLDivElement>,
  isVisible = true
) {
  const [shouldScroll, setShouldScroll] = useState(false);
  const [thumbWidth, setThumbWidth] = useState(1);

  const { scrollXProgress } = useScroll({ container: scrollContainerRef });

  // Dynamically calculate the transform range based on the thumb width
  const x = useTransform(scrollXProgress, (progress) => {
    const trackWidth = scrollTrackRef.current?.clientWidth ?? 1;
    const maxOffset = trackWidth - thumbWidth;
    return `${progress * maxOffset}px`;
  });

  useLayoutEffect(() => {
    const updateSizes = () => {
      const clientWidth = scrollContainerRef.current?.clientWidth ?? 1;
      const scrollWidth = scrollContainerRef.current?.scrollWidth ?? 1;
      const trackWidth = scrollTrackRef.current?.clientWidth ?? 1;

      // Determine if scrolling is needed
      setShouldScroll(clientWidth < scrollWidth);

      // Calculate the thumb width in pixels
      const calculatedThumbWidth = Math.min(
        (clientWidth / scrollWidth) * trackWidth,
        trackWidth
      );
      setThumbWidth(calculatedThumbWidth);
    };

    // Initial size update
    updateSizes();

    const observer = new IntersectionObserver(
      // istanbul ignore next
      ([entry]) => {
        if (entry.isIntersecting) updateSizes();
      },
      { threshold: 0.1 }
    );

    const scrollContainerElement = scrollContainerRef.current;
    if (scrollContainerElement) observer.observe(scrollContainerElement);

    window.addEventListener("resize", updateSizes);

    return () => {
      if (scrollContainerElement) observer.unobserve(scrollContainerElement);
      window.removeEventListener("resize", updateSizes);
    };
  }, [scrollContainerRef, scrollTrackRef, isVisible]);

  return { x, thumbWidth, shouldScroll };
}
